import { Mic, Pause, Play, Send, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import styles from './ChatPage.module.css';

const MAX_SEGUNDOS = 300;
const MAX_BYTES = 16 * 1024 * 1024;

export type AudioGravado = {
  blob: Blob;
  mimeType: string;
  duracaoSegundos: number;
  fileName: string;
};

type AudioRecorderButtonProps = {
  disabled?: boolean;
  onGravado: (audio: AudioGravado) => void;
  onErro?: (mensagem: string) => void;
  onGravandoChange?: (gravando: boolean) => void;
  maxSegundos?: number;
  maxBytes?: number;
};

function escolherMime(): string | undefined {
  const candidatos = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
  ];
  if (typeof MediaRecorder === 'undefined') return undefined;
  return candidatos.find((m) => MediaRecorder.isTypeSupported(m));
}

function extensaoDeMime(mime: string): string {
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4')) return 'm4a';
  return 'webm';
}

function formatTimer(segundos: number): string {
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

export function AudioRecorderButton({
  disabled,
  onGravado,
  onErro,
  onGravandoChange,
  maxSegundos = MAX_SEGUNDOS,
  maxBytes = MAX_BYTES,
}: AudioRecorderButtonProps) {
  const [gravando, setGravando] = useState(false);
  const [pausado, setPausado] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [tocandoPreview, setTocandoPreview] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const cancelRef = useRef(false);
  const enviarAoPararRef = useRef(false);
  const ativoRef = useRef(false);
  const startedAtRef = useRef(0);
  const pausadoAcumuladoRef = useRef(0);
  const pauseStartedRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);
  const mimeRef = useRef('audio/webm');
  const previewUrlRef = useRef<string | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const onGravandoChangeRef = useRef(onGravandoChange);
  onGravandoChangeRef.current = onGravandoChange;

  const limparPreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    setTocandoPreview(false);
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
  };

  const limpar = () => {
    if (tickRef.current != null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    ativoRef.current = false;
    pauseStartedRef.current = null;
    pausadoAcumuladoRef.current = 0;
    cancelRef.current = false;
    enviarAoPararRef.current = false;
    limparPreview();
    setGravando(false);
    setPausado(false);
    setSegundos(0);
    onGravandoChangeRef.current?.(false);
  };

  useEffect(() => () => limpar(), []);

  const elapsedSegundos = () => {
    const pauseExtra =
      pauseStartedRef.current != null ? Date.now() - pauseStartedRef.current : 0;
    return Math.max(
      0,
      Math.floor(
        (Date.now() - startedAtRef.current - pausadoAcumuladoRef.current - pauseExtra) / 1000,
      ),
    );
  };

  const finalizarBlob = (): AudioGravado | null => {
    const duracaoSegundos = Math.max(1, Math.min(maxSegundos, elapsedSegundos() || 1));
    const blob = new Blob(chunksRef.current, { type: mimeRef.current });
    if (blob.size === 0) return null;
    if (blob.size > maxBytes) {
      const mb = maxBytes / (1024 * 1024);
      const textoMb = Number.isInteger(mb) ? String(mb) : mb.toFixed(1);
      onErro?.(`Audio excede o limite de ${textoMb} MB.`);
      return null;
    }
    return {
      blob,
      mimeType: mimeRef.current,
      duracaoSegundos,
      fileName: `audio.${extensaoDeMime(mimeRef.current)}`,
    };
  };

  const pararRecorder = (enviar: boolean) => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      limpar();
      return;
    }
    cancelRef.current = !enviar;
    enviarAoPararRef.current = enviar;
    if (recorder.state === 'paused') {
      try {
        recorder.resume();
      } catch {
        /* ignore */
      }
    }
    recorder.stop();
  };

  const iniciar = async () => {
    if (disabled || gravando) return;
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      onErro?.('Gravacao de audio nao e suportada neste navegador.');
      return;
    }
    const mime = escolherMime();
    if (!mime) {
      onErro?.('Nenhum formato de audio suportado neste navegador.');
      return;
    }
    mimeRef.current = mime;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      recorderRef.current = recorder;
      chunksRef.current = [];
      cancelRef.current = false;
      enviarAoPararRef.current = false;
      startedAtRef.current = Date.now();
      pausadoAcumuladoRef.current = 0;
      pauseStartedRef.current = null;

      recorder.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      recorder.onstop = () => {
        const enviar = enviarAoPararRef.current && !cancelRef.current;
        const audio = enviar ? finalizarBlob() : null;
        limpar();
        if (enviar) {
          if (!audio) {
            onErro?.('Nao foi possivel gravar o audio.');
            return;
          }
          onGravado(audio);
        }
      };

      recorder.start(250);
      ativoRef.current = true;
      setGravando(true);
      setPausado(false);
      onGravandoChangeRef.current?.(true);
      tickRef.current = window.setInterval(() => {
        const elapsed = elapsedSegundos();
        setSegundos(elapsed);
        if (elapsed >= maxSegundos) {
          pararRecorder(true);
        }
      }, 200);
    } catch {
      limpar();
      onErro?.('Permissao de microfone negada ou indisponivel.');
    }
  };

  const pausarOuRetomar = () => {
    const recorder = recorderRef.current;
    if (!recorder || !ativoRef.current) return;
    if (recorder.state === 'recording') {
      try {
        recorder.requestData?.();
        recorder.pause();
        pauseStartedRef.current = Date.now();
        setPausado(true);
        limparPreview();
        const blob = new Blob(chunksRef.current, { type: mimeRef.current });
        if (blob.size > 0) {
          const url = URL.createObjectURL(blob);
          previewUrlRef.current = url;
          setPreviewUrl(url);
        }
      } catch {
        onErro?.('Pausa de gravacao nao suportada neste navegador.');
      }
      return;
    }
    if (recorder.state === 'paused') {
      if (pauseStartedRef.current != null) {
        pausadoAcumuladoRef.current += Date.now() - pauseStartedRef.current;
        pauseStartedRef.current = null;
      }
      limparPreview();
      try {
        recorder.resume();
        setPausado(false);
      } catch {
        onErro?.('Nao foi possivel retomar a gravacao.');
      }
    }
  };

  const tocarPreview = () => {
    if (!previewUrl) return;
    if (!previewAudioRef.current) {
      previewAudioRef.current = new Audio(previewUrl);
      previewAudioRef.current.onended = () => setTocandoPreview(false);
    }
    if (tocandoPreview) {
      previewAudioRef.current.pause();
      previewAudioRef.current.currentTime = 0;
      setTocandoPreview(false);
      return;
    }
    void previewAudioRef.current
      .play()
      .then(() => setTocandoPreview(true))
      .catch(() => setTocandoPreview(false));
  };

  useEffect(() => {
    if (!gravando) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') pararRecorder(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gravando]);

  if (!gravando) {
    return (
      <button
        type="button"
        className={styles.micBtn}
        disabled={disabled}
        aria-label="Gravar audio"
        onClick={() => void iniciar()}
      >
        <Mic size={18} aria-hidden />
      </button>
    );
  }

  return (
    <div className={styles.recorderBar} role="group" aria-label="Gravacao de audio">
      <button
        type="button"
        className={styles.recorderIconBtn}
        aria-label="Excluir gravacao"
        onClick={() => pararRecorder(false)}
      >
        <Trash2 size={18} aria-hidden />
      </button>
      {pausado ? (
        <button
          type="button"
          className={styles.recorderIconBtn}
          aria-label={tocandoPreview ? 'Parar preview' : 'Ouvir gravacao'}
          onClick={tocarPreview}
          disabled={!previewUrl}
        >
          {tocandoPreview ? <Pause size={18} aria-hidden /> : <Play size={18} aria-hidden />}
        </button>
      ) : null}
      <span className={styles.recorderDot} aria-hidden />
      <span className={styles.recorderStatus}>{formatTimer(segundos)}</span>
      <div className={styles.recorderSpacer} />
      <button
        type="button"
        className={`${styles.recorderIconBtn} ${pausado ? '' : styles.micBtnActive}`}
        aria-label={pausado ? 'Retomar gravacao' : 'Pausar gravacao'}
        onClick={pausarOuRetomar}
      >
        {pausado ? <Mic size={18} aria-hidden /> : <Pause size={18} aria-hidden />}
      </button>
      <button
        type="button"
        className={styles.recorderSendBtn}
        aria-label="Enviar audio"
        onClick={() => pararRecorder(true)}
      >
        <Send size={16} aria-hidden />
      </button>
    </div>
  );
}
